<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, computed, watch } from 'vue';
import type { DropdownItem } from '@/types/v2/ui/dropdown.types';

// Define props
interface Props {
  index: number;
  label?: string;
  items: DropdownItem[];
  itemKey: string;
  selectedItem?: DropdownItem | null;
  disabled?: boolean;
  placeholder?: string;
  neutralStyle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  selectedItem: null,
  disabled: false,
  placeholder: 'Select an item...',
  neutralStyle: false
});

// Define emits
const emit = defineEmits<{
  fieldChange: [event: { index: number; key: string; item: DropdownItem }];
}>();

// Local state
const isOpen = ref(false);
const dropdownRef = ref<HTMLElement>();

// Computed
const displayText = computed(() => {
  if (!props.selectedItem) return props.placeholder;
  
  // Try different property names for display text
  return props.selectedItem.name || 
         props.selectedItem.label || 
         props.selectedItem.title || 
         'Unknown';
});

const displayItems = computed(() => {
  return props.items || [];
});

const isSelectedItem = (item: DropdownItem) => {
  return !!(props.selectedItem && (props.selectedItem.key === item.key || props.selectedItem.name === item.name));
};

// Methods
const toggleDropdown = () => {
  if (!props.disabled) {
    isOpen.value = !isOpen.value;
  }
};

const selectItem = (item: DropdownItem) => {
  if (!props.disabled) {
    emit('fieldChange', {
      index: props.index,
      key: props.itemKey,
      item
    });
    isOpen.value = false;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
};

// Handle clicks outside
const handleClickOutside = (event: Event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

// Watch for outside clicks
watch(isOpen, (newVal) => {
  if (newVal) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});
</script>

<template>
  <div class="relative" ref="dropdownRef">
    <!-- Label -->
    <label v-if="label" class="block text-sm font-medium text-gray-700 mb-1">
      {{ label }}
    </label>
    
    <!-- Dropdown button -->
    <button
      @click="toggleDropdown"
      :disabled="disabled"
      class="flex items-center justify-between w-full min-w-[150px] text-sm border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-150"
      :class="{
        'focus:ring-blue-500 focus:border-blue-500': !neutralStyle,
        'focus:ring-gray-300 focus:border-gray-400': neutralStyle,
        'bg-gray-50': disabled,
        'cursor-not-allowed': disabled
      }"
    >
      <span class="truncate flex-1 text-left" :class="{ 'text-gray-500': !selectedItem }">
        {{ displayText }}
      </span>
      <svg 
        class="h-4 w-4 text-gray-500 ml-2 flex-shrink-0 transition-transform duration-150"
        :class="{ 'rotate-180': isOpen }"
        xmlns="http://www.w3.org/2000/svg" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    
    <!-- Dropdown menu -->
    <div 
      v-if="isOpen" 
      class="absolute z-50 mt-1 w-full min-w-[200px] bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
    >
      <div v-if="displayItems.length === 0" class="px-3 py-2 text-sm text-gray-500">
        No items available
      </div>
      <template v-else>
        <button 
          v-for="item in displayItems" 
          :key="item.key || item.name || item.label"
          @click="selectItem(item)"
          :disabled="disabled"
          class="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          :class="[
            neutralStyle
              ? (isSelectedItem(item) ? 'bg-gray-100 text-gray-900' : '')
              : (isSelectedItem(item) ? 'bg-blue-50 text-blue-700' : '')
          ]"
        >
          {{ item.name || item.label || item.title || 'Unknown' }}
        </button>
      </template>
    </div>
  </div>
</template> 
